import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CaptureProvider } from "@/context/CaptureContext";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import Login from "@/pages/Login";
import Home from "@/pages/Home";
import Inbox from "@/pages/Inbox";
import Catalog from "@/pages/Catalog";
import CaptureDetail from "@/pages/CaptureDetail";
import Connect from "@/pages/Connect";
import CollectionDetail from "@/pages/CollectionDetail";
import Create from "@/pages/Create";
import Collaborate from "@/pages/Collaborate";
import Continue from "@/pages/Continue";
import Search from "@/pages/Search";
import Trash from "@/pages/Trash";
import Settings from "@/pages/Settings";
import { Compass } from "@/lib/icons";

function OfflineSync() {
  const [pending, setPending] = useState([]);

  const read = () => setPending(JSON.parse(localStorage.getItem("dl_pending_drafts") || "[]"));
  useEffect(() => {
    read();
    window.addEventListener("dl-pending-changed", read);
    const sync = async () => {
      const drafts = JSON.parse(localStorage.getItem("dl_pending_drafts") || "[]");
      if (!drafts.length) return;
      const remaining = [];
      for (const d of drafts) {
        try {
          await api.post("/captures", {
            title: d.title, text: d.text, type: "note",
            tags: (d.tags || "").split(",").map((t) => t.trim()).filter(Boolean),
            collection_ids: [], client_token: d.id,
          });
        } catch { remaining.push(d); }
      }
      localStorage.setItem("dl_pending_drafts", JSON.stringify(remaining));
      read();
      window.dispatchEvent(new Event("dl-saved"));
    };
    window.addEventListener("online", sync);
    if (navigator.onLine) sync();
    return () => { window.removeEventListener("online", sync); window.removeEventListener("dl-pending-changed", read); };
  }, []);

  if (!pending.length) return null;
  return (
    <div className="fixed bottom-20 lg:bottom-4 left-1/2 -translate-x-1/2 z-40 px-4 py-2 rounded-full text-xs font-medium backdrop-blur-xl" style={{ background: "rgba(245,158,11,0.9)", color: "#1a1206" }} data-testid="pending-banner">
      {pending.length} local draft{pending.length > 1 ? "s" : ""} pending — will sync when online
    </div>
  );
}

function Shell() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen grid place-items-center" style={{ background: "var(--dl-bg)" }}>
        <div className="flex flex-col items-center gap-3 text-slate-400">
          <div className="grid place-items-center h-12 w-12 rounded-xl animate-pulse" style={{ background: "linear-gradient(135deg,#2563EB,#0EA5E9)" }}><Compass size={24} className="text-white" /></div>
          <span className="text-sm">Loading your workspace…</span>
        </div>
      </div>
    );
  }
  if (user === false) return <Login />;
  return (
    <CaptureProvider onSaved={() => window.dispatchEvent(new Event("dl-saved"))}>
      <OfflineSync />
      <Routes>
        <Route element={<AppLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/inbox" element={<Inbox />} />
          <Route path="/collect" element={<Inbox />} />
          <Route path="/catalog" element={<Catalog />} />
          <Route path="/captures/:id" element={<CaptureDetail />} />
          <Route path="/connect" element={<Connect />} />
          <Route path="/collections/:id" element={<CollectionDetail />} />
          <Route path="/curate" element={<Create />} />
          <Route path="/communicate" element={<Collaborate />} />
          <Route path="/create" element={<Navigate to="/curate" replace />} />
          <Route path="/collaborate" element={<Navigate to="/communicate" replace />} />
          <Route path="/continue" element={<Continue />} />
          <Route path="/search" element={<Search />} />
          <Route path="/trash" element={<Trash />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<Home />} />
        </Route>
      </Routes>
    </CaptureProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Shell />
        <Toaster position="top-center" theme="dark" />
      </BrowserRouter>
    </AuthProvider>
  );
}
